import { useQuery, gql, TypedDocumentNode } from '@apollo/client'
import React from 'react'
import { Product as ProductSchema } from 'schema-dts'

import { getCacheableContext } from '@emico/apollo'
import { useCustomAttributeByValue } from '@emico/custom-attribute'
import { ProductStockStatus, ProductImage } from '@emico/graphql-schema-types'
import { ProductDetailsFragment } from '@emico/product-details-fragment'

import JsonLd from './JsonLd'
import {
  StoreConfigSchemaOrgQuery,
  StoreConfigSchemaOrgQueryVariables,
} from './Product.generated'

type ValidImage = ProductImage & { disabled: false; url: string }


const isValidImage = (
    image: Pick<ProductImage, 'disabled' | 'url'> | null | undefined,
  ): boolean => Boolean(image && !image.disabled && image.url)
  
  

const getMediaItems = (product: ProductDetailsFragment) => {
    const mediaGallery = product?.mediaGallery?.filter(
      (image): image is ValidImage => isValidImage(image),
    )
    if (mediaGallery && mediaGallery.length > 0) {
      return mediaGallery.sort((a, b) => (a.position || 0) - (b.position || 0))
    }
    if (isValidImage(product?.smallImage)) {
      return [product?.smallImage as ValidImage]
    }
    return []
  }
  

const query = gql`
  query StoreConfigSchemaOrg {
    storeConfig {
      storeName
    }
  }
` as TypedDocumentNode<
  StoreConfigSchemaOrgQuery,
  StoreConfigSchemaOrgQueryVariables
>

type Props = Partial<ProductSchema> & {
  product: Exclude<ProductDetailsFragment, null>
}

const Product = ({ product, ...others }: Props) => {
  const { data } = useQuery(query, {
    context: getCacheableContext(),
  })

  const { data: manufacturerName } = useCustomAttributeByValue(
    'manufacturer',
    String(product.manufacturer),
  )

  if (!data?.storeConfig) {
    return null
  }

  if (!product.canonicalUrl) {
    console.warn(
      'Magento 2 is not configured properly: "Use Canonical Link Meta Tag For Products" must be enabled.' +
        'Currently the Open Graph urls and ids are rendered via the urlKey which may result in duplicate content.',
    )
  }

  const mediaItems = getMediaItems(product)
  const price = product.priceRange.minimumPrice.finalPrice

  return (
    <JsonLd<ProductSchema>
      item={{
        '@context': 'https://schema.org',
        '@type': 'Product',
        '@id': product.canonicalUrl ?? product.urlKey,
        name: product.name,
        image: mediaItems.map((item) => item.url),
        url: product.canonicalUrl ?? product.urlKey,
        brand: manufacturerName?.label
          ? { '@type': 'Brand', name: manufacturerName?.label }
          : undefined,
        description: product.shortDescription?.html,
        sku: product.sku,
        offers: {
          '@type': 'Offer',
          priceCurrency: price.currency,
          price: price.value,
          availability:
            product.stockStatus === ProductStockStatus.IN_STOCK
              ? 'https://schema.org/InStock'
              : 'https://schema.org/OutOfStock',
          itemCondition: 'https://schema.org/NewCondition',
        },
        ...others,
      }}
    />
  )
}

export default Product
